import React, { useState, useEffect } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import { getQueryParams } from "lib/utils";
import { useTranslation } from "react-i18next";
import FormContainer from "components/form-container";
import InputValidation from "components/input-validation";
import Checkbox from "components/checkbox";
import TermsText from "components/terms-text";
import { useRef } from "react";

export default function DropoffSellerTermsConditions() {
  const location = useLocation();
  const [isBottom, setIsBottom] = useState(false);
  const [warning, setWarning] = useState(false);
  const queryParams = getQueryParams(location.search);
  const { t } = useTranslation();
  const { setSidebarVisual } = useOutletContext();
  const containerRef = useRef(null);

  // Checkbox state
  const [agreements, setAgreements] = useState({
    terms: false,
    ageConfirmation: false,
    transferOwnership: false,
  });

  // Mock data
  const firstName = queryParams.firstName;

  // Set sidebar visual
  useEffect(() => {
    setSidebarVisual("dollar");
  }, [setSidebarVisual]);

  const handleCheckboxChange = (name) => {
    setAgreements((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setIsBottom(true);
      setWarning(false);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const allAgreed = Object.values(agreements).every(Boolean);

  return (
    <FormContainer
      submitLabel="Confirm Trade-In"
      submitHref="/en-US/dropoff-seller/completion"
      submitParams={queryParams}
      submitDisabled={!allAgreed}
      secondaryLabel={t("ui.button_back")}
      secondaryHref="/en-US/dropoff-seller/confirm-details"
      secondaryParams={queryParams}
    >
      <div className="flex flex-col gap-regular">
        <h2 className="text-primary text-4xl md:text-6xl font-semibold">
          Hey, {firstName}
        </h2>
        <p className="text-tertiary text-xl md:text-2xl">
          Please review and agree to the Terms and Conditions.
        </p>
      </div>

      {/* Terms and Conditions */}
      <div className="w-full flex flex-col gap-large">
        <h3 className="mb-regular text-2xl font-semibold">
          Terms and Conditions
        </h3>

        <div
          data-testid="terms"
          ref={containerRef}
          className={`overflow-y-scroll h-[300px] w-full border bg-gray-300/10 text-sm p-4 rounded-xl${
            warning ? " border-red" : " border-gray-400"
          }`}
        >
          <TermsText scroll={false} />
        </div>
        {warning && (
          <InputValidation errorMessage={t("trade-in.terms.warning")} />
        )}

        {/* Checkboxes */}
        <div
          className="space-y-4"
          onClick={!isBottom ? () => setWarning(true) : () => setWarning(false)}
        >
          <Checkbox
            disabled={!isBottom}
            checked={agreements.terms}
            setChecked={() => handleCheckboxChange("terms")}
            label="I accept and have read all of the Terms and Conditions above"
          />

          <Checkbox
            checked={agreements.ageConfirmation}
            setChecked={() => handleCheckboxChange("ageConfirmation")}
            label="I confirm that I am at least 18 years old and that I am the owner of the LEGO® bricks and elements I am providing for trade-in purposes or have the legal right to transfer title of these LEGO bricks and elements."
          />

          <Checkbox
            checked={agreements.transferOwnership}
            setChecked={() => handleCheckboxChange("transferOwnership")}
            label="I understand that each time my LEGO bricks are cleared from the weighing tray into the kiosk, they are no longer retrievable, and as such, I am agreeing to the transfer of ownership."
          />
        </div>
      </div>
    </FormContainer>
  );
}
